import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { normalizeWorkerIdString } from '../utils/workerAgentIds';
import { hasAnyPersistedOptimizationJobId } from '../utils/optimizationJobLifecycle';
import { OPTIMIZATION_STORE_VERSION, migrateOptimizationStore, rehydrationPatch } from './rehydration';
import { mergeConfiguratorModelOptions } from '../utils/configuratorModels';
import { createSingleAgentSlice, type SingleAgentSlice } from './slices/singleAgentSlice';
import { createMultiAgentSlice, type MultiAgentSlice } from './slices/multiAgentSlice';
import { createConnectorSlice, type ConnectorSlice } from './slices/connectorSlice';

import type { WorkerAgentConfig } from './storeShared';

export type { ChatMessage } from './storeShared';

/** The optimization store is three slices (AIT-423): single-agent + shared settings, the
 *  multi-agent page, and the Connector configurator. Read it with selectors. */
export type OptimizationState = SingleAgentSlice & MultiAgentSlice & ConnectorSlice;

export const useOptimizationStore = create<OptimizationState>()(
  persist(
    (...a) => ({
      ...createSingleAgentSlice(...a),
      ...createMultiAgentSlice(...a),
      ...createConnectorSlice(...a),
    }),
    {
      name: 'optimization-store',
      merge: (persisted, current) => {
        const p = persisted as Partial<OptimizationState> | undefined;
        const base = { ...current, ...(p ?? {}) };
        const mapIds = (list: WorkerAgentConfig[] | undefined, fallback: WorkerAgentConfig[]) =>
          Array.isArray(list) ? list.map((w, i) => ({ ...w, id: normalizeWorkerIdString(w.id, i) })) : fallback;
        return {
          ...base,
          availableModels: mergeConfiguratorModelOptions(
            Array.isArray(p?.availableModels) ? p!.availableModels : current.availableModels
          ),
          connectorReminderPromptPosition: p?.connectorReminderPromptPosition === 'after' ? 'after' : 'before',
          workers: mapIds(base.workers, current.workers),
          connectorWorkers: mapIds(base.connectorWorkers, current.connectorWorkers),
          multiAgentDefaults: base.multiAgentDefaults
            ? {
                ...base.multiAgentDefaults,
                coordinatorName:
                  base.multiAgentDefaults.coordinatorName ??
                  current.multiAgentDefaults?.coordinatorName ??
                  'Coordinator',
                workers: mapIds(base.multiAgentDefaults.workers, current.multiAgentDefaults.workers),
              }
            : current.multiAgentDefaults,
        };
      },
      // Исключаем из сохранения временные состояния оптимизации
      partialize: (state: OptimizationState) =>
        ({
          // Основные настройки пользователя
          selectedDataset: state.selectedDataset,
          selectedDatasets: state.selectedDatasets,
          selectedModel: state.selectedModel,
          temperature: state.temperature,
          iterations: state.iterations,
          multiAgentIterations: state.multiAgentIterations,

          // Экспоненциальный прогресс (сохраняем для восстановления при обновлении страницы)
          progressStartTime: state.progressStartTime,
          currentProgressStage: state.currentProgressStage,
          expectedIterationTime: state.expectedIterationTime,
          isOptimizing: state.isOptimizing,
          // ⚠️ НЕ сохраняем optimizationProgress - он пересчитывается в onRehydrateStorage!
          // Это предотвращает проблему с "застрявшим" 100% при повторном запуске оптимизации

          // Экспоненциальный прогресс для multi-agent
          multiAgentProgressStartTime: state.multiAgentProgressStartTime,
          multiAgentCurrentProgressStage: state.multiAgentCurrentProgressStage,
          multiAgentExpectedIterationTime: state.multiAgentExpectedIterationTime,
          multiAgentIsOptimizing: state.multiAgentIsOptimizing,
          // ⚠️ НЕ сохраняем multiAgentOptimizationProgress - он пересчитывается в onRehydrateStorage!

          // Мультиагентные настройки
          workers: state.workers,
          coordinatorPrompt: state.coordinatorPrompt,
          coordinatorModel: state.coordinatorModel,
          coordinatorTemperature: state.coordinatorTemperature,
          coordinatorDescription: state.coordinatorDescription,
          coordinatorReminderPrompt: state.coordinatorReminderPrompt,
          coordinatorName: state.coordinatorName,
          additionalWorkerConfig: state.additionalWorkerConfig,
          multiAgentDefaults: state.multiAgentDefaults,

          // Конфигурация для ConnectorPage
          connectorWorkers: state.connectorWorkers,
          connectorCoordinatorPrompt: state.connectorCoordinatorPrompt,
          connectorCoordinatorModel: state.connectorCoordinatorModel,
          connectorCoordinatorTemperature: state.connectorCoordinatorTemperature,
          connectorCoordinatorDescription: state.connectorCoordinatorDescription,
          connectorCoordinatorReminderPrompt: state.connectorCoordinatorReminderPrompt,
          connectorCoordinatorName: state.connectorCoordinatorName,
          connectorCoordinatorBackendId: state.connectorCoordinatorBackendId,
          connectorCoordinatorAgentId: state.connectorCoordinatorAgentId,
          connectorReminderPromptPosition: state.connectorReminderPromptPosition,
          connectorConfigLabel: state.connectorConfigLabel,

          // Доступные опции
          availableDatasets: state.availableDatasets,
          availableModels: state.availableModels,
          iterationsMax: state.iterationsMax,

          // Промпты пользователя
          initialPrompt: state.initialPrompt,
          botPrompt: state.botPrompt,

          // Результаты оптимизации для single-agent - критично для работы чата After Optimization
          iterationResults: state.iterationResults,
          isOptimized: state.isOptimized,
          optimizedPrompts: state.optimizedPrompts,
          selectedIterationIndex: state.selectedIterationIndex,

          // Флаг инициализации
          isDefaultsLoaded: state.isDefaultsLoaded,

          // Чаты - сохраняем между сессиями и переключениями страниц
          singleAgentChatMessages: state.singleAgentChatMessages,
          singleAgentChatInputs: state.singleAgentChatInputs,
          multiAgentChatMessages: state.multiAgentChatMessages,
          multiAgentChatInputs: state.multiAgentChatInputs,

          // Мультиагентные результаты оптимизации - критично для работы чата After Optimization
          multiAgentIterationResults: state.multiAgentIterationResults,
          multiAgentIsOptimized: state.multiAgentIsOptimized,
          multiAgentSelectedIterationIndex: state.multiAgentSelectedIterationIndex,
          multiAgentSelectedPrompt: state.multiAgentSelectedPrompt,
          multiAgentSelectedPromptIndex: state.multiAgentSelectedPromptIndex,
          multiAgentGraphSliderValue: state.multiAgentGraphSliderValue,
          multiAgentOptimizedPrompts: state.multiAgentOptimizedPrompts,

          // Экспоненциальный прогресс для connector multi-agent
          connectorMultiAgentProgressStartTime: state.connectorMultiAgentProgressStartTime,
          connectorMultiAgentCurrentProgressStage: state.connectorMultiAgentCurrentProgressStage,
          connectorMultiAgentExpectedIterationTime: state.connectorMultiAgentExpectedIterationTime,
          connectorMultiAgentIsOptimizing: state.connectorMultiAgentIsOptimizing,
          // ⚠️ НЕ сохраняем connectorMultiAgentOptimizationProgress - он пересчитывается в onRehydrateStorage!

          // Connector evaluation results — session-only (avoid stale Initial/Optimized on page open).
          // Loaded explicitly via optimization run, job polling, or Profile → Load to Connector.

          // Чат для Connector Multi-Agent
          connectorMultiAgentChatMessages: state.connectorMultiAgentChatMessages,
          connectorMultiAgentChatInputs: state.connectorMultiAgentChatInputs,
        }) as Partial<OptimizationState>,
      // Schema version and migration of older snapshots (AIT-423).
      version: OPTIMIZATION_STORE_VERSION,
      migrate: (persisted, version) => migrateOptimizationStore(persisted, version) as unknown as OptimizationState,
      // Recompute progress and drop stale run state once rehydrated. The store is still being
      // created during a synchronous rehydration, so the patch is applied one microtask later.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const patch = rehydrationPatch(state, { hasPersistedOptimizationJob: hasAnyPersistedOptimizationJobId() });
        queueMicrotask(() => useOptimizationStore.setState(patch));
      },
    }
  )
);
